import { useCallback, useEffect, useState } from 'react'
import { preferencesStore, type PreferencesListener } from '../services/preferencesStore'
import type { ResolvedPreferences, UserPreferences } from '../types'

/**
 * React hook for user preferences (Track D)。
 *
 * - `preferences`: 常に Required で穴のない snapshot (defaults で埋まる)
 * - `setPreference(key, value)`: optimistic 更新 + API + IndexedDB cache。失敗時は revert
 * - mount 時に `loadInitial()` を呼び、cache → API の順で正値で埋める。
 *   loadInitial は idempotent (二度呼んでも override されるだけ) なので、複数 hook
 *   instance が並列 mount されても問題ない
 */

export interface UseUserPreferences {
  preferences: ResolvedPreferences
  setPreference: <K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K],
  ) => Promise<void>
}

export const useUserPreferences = (): UseUserPreferences => {
  const [preferences, setPreferences] = useState<ResolvedPreferences>(() =>
    preferencesStore.getResolved(),
  )

  useEffect(() => {
    const listener: PreferencesListener = next => {
      setPreferences(next)
    }

    const unsubscribe = preferencesStore.subscribe(listener)

    void preferencesStore.loadInitial()

    return unsubscribe
  }, [])

  const setPreference = useCallback(
    <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]): Promise<void> =>
      preferencesStore.setPreference(key, value),
    [],
  )

  return { preferences, setPreference }
}
